import type { UseFormReturnType } from '@mantine/form'
import type { FormPathValue } from '@mantine/form/lib/paths.types'
import { Autocomplete, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { UIFormLabel, UIFormTextField } from '../ui/Form'
import { UIInput, UITextArea } from '../ui/TextField'

const defaultGeneratorOptions = [
  'NovelAI',
  'Waifu Diffusion',
  'Stable Diffusion',
  'TrinArt',
  'Midjourney',
  'Dalle-2',
  'Ernie-vilg',
  'Unstable Diffusion',
  'Hentai Diffusion',
  'Custom Model',
  'niji・journey',
  'Anything3',
  'Meitu AI Painting',
  'Rivera',
  'Holara',
  'ACertainThing',
  'Anything4',
  'CounterfeitV2',
]

export type DetailParamsEditFormValues = {
  negativePrompts: string
  samplingSteps: number
  samplingMethod: string
  cfgScale: number
  seed: number
  generator: string
}

export type DetailParamsEditFormProps<
  TFormValues extends {
    extra?: {
      externalParameters?: DetailParamsEditFormValues
    }
  },
> = {
  form: UseFormReturnType<TFormValues>
}

export const DetailParamsEditFormItems = <
  TFormValues extends {
    extra?: {
      externalParameters?: DetailParamsEditFormValues
    }
  },
>({
  form,
}: DetailParamsEditFormProps<TFormValues>) => {
  const { t } = useTranslation(['generation'])

  return (
    <>
      {/* negativePrompts */}
      <UIFormTextField
        input={form.getInputProps('extra.externalParameters.negativePrompts')}
        layout="vertical"
      >
        {input => (
          <>
            <UIFormLabel>{t('generation:negative-prompts.label')}</UIFormLabel>
            <UITextArea
              variant="rounded-normal"
              value={input.value || ''}
              defaultValue={input.defaultValue}
              onChange={e =>
                form.setFieldValue(
                  'extra.externalParameters.negativePrompts',
                  e.target.value as FormPathValue<
                    TFormValues,
                    'extra.externalParameters.negativePrompts'
                  >,
                )
              }
              rows={3}
            />
          </>
        )}
      </UIFormTextField>

      {/* samplingSteps */}
      <UIFormTextField
        input={form.getInputProps('extra.externalParameters.samplingSteps')}
        layout="vertical"
      >
        {input => (
          <>
            <UIFormLabel>{t('generation:sampling-steps.label')}</UIFormLabel>
            <UIInput {...input} type="number" value={input.value || ''} />
          </>
        )}
      </UIFormTextField>

      {/* samplingMethod */}
      <UIFormTextField
        input={form.getInputProps('extra.externalParameters.samplingMethod')}
        layout="vertical"
      >
        {input => (
          <>
            <UIFormLabel>{t('generation:sampling-method.label')}</UIFormLabel>
            <UIInput {...input} value={input.value || ''} />
          </>
        )}
      </UIFormTextField>

      {/* cfgScale */}
      <UIFormTextField
        input={form.getInputProps('extra.externalParameters.cfgScale')}
        layout="vertical"
      >
        {input => (
          <>
            <UIFormLabel>{t('generation:cfg-scale.label')}</UIFormLabel>
            <UIInput {...input} type="number" value={input.value || ''} />
          </>
        )}
      </UIFormTextField>

      {/* seed */}
      <UIFormTextField
        input={form.getInputProps('extra.externalParameters.seed')}
        layout="vertical"
      >
        {input => (
          <>
            <UIFormLabel>{t('generation:seed.label')}</UIFormLabel>
            <UIInput {...input} value={input.value || ''} />
          </>
        )}
      </UIFormTextField>

      {/* generator */}
      <div className="flex flex-col gap-2">
        <UIFormLabel>{t('generation:generator.label')}</UIFormLabel>
        <Autocomplete
          freeSolo
          value={
            form.getInputProps('extra.externalParameters.generator').value || ''
          }
          onChange={(_, v) =>
            form.setFieldValue('extra.externalParameters.generator', v)
          }
          options={defaultGeneratorOptions}
          renderInput={params => <TextField {...params} size="small" />}
        />
      </div>
    </>
  )
}
